import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { wrapFunctionalComponent } from 'utils/framework';
import { Button, Grid, Text } from 'components/ui';
import Modal from 'components/Modal/Modal';
import Actions from 'actions/Actions';
import addNoteService from 'services/api/utility/addNote';
import Textarea from 'components/Inputs/Textarea/Textarea';

function AddNoteModal({ isOpen, noteType }) {
    const dispatch = useDispatch();
    const [description, setDescription] = useState('');
    const [saveNoteError, setSaveNoteError] = useState('');
    const [isSaving, setIsSaving] = useState(false);

    // Reset state when modal opens
    useEffect(() => {
        if (isOpen) {
            setDescription('');
            setSaveNoteError('');
            setIsSaving(false);
        }
    }, [isOpen]);

    const handleDescriptionChange = e => {
        setDescription(e.currentTarget.value);
        setSaveNoteError('');
    };

    const handleCloseConfirmation = () => {
        // Reset state and close modal
        setDescription('');
        setSaveNoteError('');
        dispatch(Actions.showAddNoteModal({ isOpen: false }));
    };

    const requestClose = () => {
        // Show confirmation modal if there's unsaved content or an error
        if (description.length >= 1 || saveNoteError) {
            dispatch(
                Actions.showInfoModal({
                    isOpen: true,
                    title: 'Are You Sure?',
                    message: 'If you leave now, the message will not be saved.',
                    buttonText: 'Yes, Don\'t Save',
                    onButtonClick: handleCloseConfirmation
                })
            );
        } else {
            dispatch(Actions.showAddNoteModal({ isOpen: false }));
        }
    };

    const handleSave = async () => {
        setIsSaving(true);
        setSaveNoteError('');

        try {
            await addNoteService(noteType, description);
            requestClose();
            // Show success modal
            dispatch(
                Actions.showInfoModal({
                    isOpen: true,
                    title: 'Your Note has been Saved!',
                    message: 'The client\'s profile note has been saved and can be viewed under their account in C3.',
                    buttonText: 'OK'
                })
            );
        } catch (error) {
            setSaveNoteError(error.message || 'Message was not saved. Please try again.');
            setIsSaving(false);
        }
    };

    const disabled = description.length < 1 || isSaving;

    const titles = {
        order: 'Add Order Notes',
        profile: 'Add Account Notes'
    };

    const placeholders = {
        order: 'Provide order details and contact method.',
        profile: 'Provide profile detail notes and contact method.'
    };

    return (
        <Modal
            isOpen={isOpen}
            onDismiss={requestClose}
            showDismiss={true}
            width={400}
            isDrawer={true}
        >
            <Modal.Header>
                <Modal.Title>{titles[noteType]}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Textarea
                    placeholder={placeholders[noteType]}
                    value={description}
                    onChange={handleDescriptionChange}
                    maxLength={1000}
                    hideCharacterCount={false}
                    customStyle={{
                        root: { marginBottom: 0 },
                        input: { height: '137px', resize: 'none' }
                    }}
                />
                {saveNoteError && (
                    <Text
                        is='p'
                        style={styles.error}
                    >
                        {saveNoteError}
                    </Text>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Grid
                    columns={2}
                    gap={2}
                >
                    <Button
                        variant='secondary'
                        onClick={requestClose}
                        block={true}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant={'primary'}
                        onClick={handleSave}
                        disabled={disabled}
                        block={true}
                    >
                        {isSaving ? 'Saving...' : 'Save'}
                    </Button>
                </Grid>
            </Modal.Footer>
        </Modal>
    );
}

const styles = {
    error: {
        marginTop: '16px',
        color: '#CF112C',
        marginBottom: 0
    }
};

export default wrapFunctionalComponent(AddNoteModal, 'AddNoteModal');
